import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Last Updated: March 31, 2026

1. Information We Collect
When you purchase The Cortisol Reset Protocol, we collect only the information necessary to process your order, including your name and email address. We do not collect sensitive health data through our website.

2. How We Use Your Information
We use your information solely to deliver your digital product, send purchase confirmations, and provide any relevant product updates. We will never sell, rent, or share your personal information with third parties for marketing purposes.

3. Cookies & Tracking
Our website may use essential cookies to ensure proper functionality and anonymous analytics to improve user experience. No personally identifiable information is collected through cookies.

4. Data Security
We implement industry-standard security measures to protect your personal information. All transactions are processed through secure, encrypted payment processors. We do not store payment card details on our servers.

5. Your Rights
You have the right to request access to, correction of, or deletion of your personal data at any time. You may also opt out of any marketing communications.

6. Third-Party Services
We may use third-party services for payment processing and product delivery. These services have their own privacy policies and handle data in accordance with their respective terms.

7. Changes to This Policy
We reserve the right to update this Privacy Policy at any time. Any changes will be reflected on this page with an updated revision date.
`.trim();
const termsContent = `
Last Updated: March 31, 2026

1. Product Description
The Cortisol Reset Protocol is a digital educational guide providing wellness information related to stress management and sleep improvement. It is delivered electronically upon purchase.

2. Educational Disclaimer
This product is for educational and informational purposes only. It is not intended to diagnose, treat, cure, or prevent any disease or medical condition. The content does not constitute medical advice. Always consult a qualified healthcare professional before beginning any new health protocol, especially if you have existing medical conditions or are taking medication.

3. No Guarantees
While the protocol is based on wellness research and user experiences, individual results may vary. We do not guarantee specific health outcomes from using this product.

4. Digital Product & Refund Policy
As this is a digital product with immediate access upon purchase, all sales are final. If you experience technical issues accessing your product, we will work to resolve them promptly.

5. Intellectual Property
All content within The Cortisol Reset Protocol, including text, graphics, and methods, is protected by copyright. You may not reproduce, distribute, or resell any portion of this product without written permission.

6. Limitation of Liability
To the fullest extent permitted by law, The Cortisol Reset Protocol and its creators shall not be held liable for any damages arising from the use or inability to use this product. Use of this product is at your own risk.

7. Governing Law
These terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through appropriate legal channels.

8. Acceptance of Terms
By purchasing and using The Cortisol Reset Protocol, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-navy-900 border-t border-navy-800 text-cream-400/60 py-12 px-6 text-center text-sm font-light" data-id="element-27">
        <div className="container mx-auto max-w-4xl" data-id="element-28">
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-29">
            <button onClick={() => setActiveModal('privacy')} className="text-cream-300/60 hover:text-gold-500 transition-colors duration-200 underline underline-offset-4 decoration-cream-300/20 hover:decoration-gold-500/50" data-id="element-30">
              Privacy Policy
            </button>
            <span className="text-cream-400/30" data-id="element-31">•</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream-300/60 hover:text-gold-500 transition-colors duration-200 underline underline-offset-4 decoration-cream-300/20 hover:decoration-gold-500/50" data-id="element-32">
              Terms of Service
            </button>
          </div>

          <p className="mb-4" data-id="element-33">
            &copy; {new Date().getFullYear()} The Cortisol Reset Protocol. All
            rights reserved.
          </p>
          <p className="max-w-2xl mx-auto leading-relaxed" data-id="element-34">
            Disclaimer: The information provided in this protocol is for
            educational purposes only and is not intended as a substitute for
            medical advice, diagnosis, or treatment. Always consult with your
            physician or other qualified health provider regarding any medical
            condition or treatment.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-35">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-36">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="bg-cream-50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-37">
              <div className="flex items-center justify-between px-8 py-6 border-b border-cream-200" data-id="element-38">
                <h2 className="font-serif text-2xl text-navy-900" data-id="element-39">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-navy-700/50 hover:text-navy-900 transition-colors p-1" aria-label="Close" data-id="element-40">
                  <XIcon size={24} data-id="element-41" />
                </button>
              </div>

              <div className="px-8 py-6 overflow-y-auto text-left" data-id="element-42">
                <div className="text-navy-700/80 text-sm leading-relaxed whitespace-pre-line font-light" data-id="element-43">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </div>
              </div>

              <div className="px-8 py-4 border-t border-cream-200 text-center" data-id="element-44">
                <button onClick={() => setActiveModal(null)} className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-medium px-6 py-2.5 rounded-sm transition-colors duration-200 text-sm tracking-wide" data-id="element-45">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}